/**
 * Murojaat yuborish — Public reporting (ЧТЗ 4.2.11.3).
 *
 * Avtorizatsiyasiz (OneID talab qilinmaydi). Majburiy: murojaat turi, telefon
 * (+998, SMS uchun) va xaritada nuqta — nuqtasiz murojaat yuborilmaydi. Nuqta
 * belgilangach koordinata va u tushgan hudud (viloyat, tuman, o'rmon xo'jaligi)
 * ko'rsatiladi; «Mening joylashuvim» brauzer ruxsati bilan ishlaydi, aniqlangan
 * nuqtani sudrab yoki xaritani bosib o'zgartirish mumkin. Ixtiyoriy: izoh
 * (1000 belgigacha), foto/video (JPG/PNG — 20 MB, MP4 — 50 MB gacha, ko'pi bilan
 * 5 ta) — har birini ko'rish, almashtirish, o'chirish; yuborishda yuklanish foizi.
 *
 * Backendda: bazaga saqlash, Kuzatuv operatoriga yetkazish, ID bilan SMS,
 * antivirus, reCAPTCHA, IP (5/soat) va telefon (3/sutka) cheklovi.
 *
 * Yuborilgach: `SF-INC-ГГГГ-######` ID, qabul qilingan sana va «Yangi» holati
 * ko'rsatiladi. Xatolar faqat rang bilan emas — matn va ikonka bilan
 * ko'rsatiladi (WCAG 1.4.1, 3.3.1).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import type { ChangeEvent, FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { ApiError } from "@/shared/api/client";
import { appealsApi } from "@/shared/api/endpoints";
import type { AppealCreated, AppealPlace, PublicHome } from "@/shared/api/types";
import { publicBasemapFallback } from "@/shared/data/landing";
import { useAppealTypes } from "@/shared/data/portal";
import { useI18n } from "@/shared/i18n";
import { useCaptcha } from "@/shared/lib/captcha";
import { formatDate } from "@/shared/lib/format";
import { SECTION_PATHS } from "@/shared/lib/sectionRoutes";
import { Icon } from "@/shared/ui/Icon";
import { MapView } from "@/shared/ui/MapView";
import type { MapFocus } from "@/shared/ui/MapView";
import { Modal } from "@/shared/ui/Modal";

import { SplitHead } from "./SplitHead";
import { usePublicMap } from "./publicData";

const MB = 1024 * 1024;
const MAX_FILES = 5;
const MAX_COMMENT = 1000;
const FILE_RULES: Record<string, { mime: string; maxMb: number; label: string }> = {
  jpg: { mime: "image/jpeg", maxMb: 20, label: "foto" },
  jpeg: { mime: "image/jpeg", maxMb: 20, label: "foto" },
  png: { mime: "image/png", maxMb: 20, label: "foto" },
  mp4: { mime: "video/mp4", maxMb: 50, label: "video" },
};
const FILE_ACCEPT = ".jpg,.jpeg,.png,.mp4,image/jpeg,image/png,video/mp4";

/** O'zbekiston operator va shahar kodlari (+998 dan keyingi 2 raqam). */
const PHONE_CODES = new Set([
  "20", "33", "50", "55", "61", "62", "65", "66", "67", "69", "70", "71", "72", "73", "74",
  "75", "76", "77", "78", "79", "88", "90", "91", "93", "94", "95", "97", "98", "99",
]);

type Field = "type" | "phone" | "location" | "comment" | "files" | "consent" | "captcha";
type Errors = Partial<Record<Field, string>>;

interface Attachment {
  id: number;
  file: File;
  /** Ko'rish uchun brauzer xotirasidagi havola (`blob:`) — o'chirilganda bo'shatiladi. */
  url: string;
  kind: "photo" | "video";
}

type UploadPhase = "idle" | "uploading" | "done" | "failed";

interface PlaceState {
  phase: "idle" | "loading" | "done" | "error";
  data?: AppealPlace & { found: boolean };
}

/** 9 ta raqamni «(90) 123-45-67» ko'rinishiga keltiradi. */
function formatPhone(digits: string): string {
  const parts = [digits.slice(0, 2), digits.slice(2, 5), digits.slice(5, 7), digits.slice(7, 9)];
  let text = "";
  if (parts[0]) text += `(${parts[0]}${parts[0].length === 2 ? ")" : ""}`;
  if (parts[1]) text += ` ${parts[1]}`;
  if (parts[2]) text += `-${parts[2]}`;
  if (parts[3]) text += `-${parts[3]}`;
  return text;
}

/** Telefon xatosi (bo'sh satr — raqam to'g'ri). */
function phoneProblem(digits: string): string {
  if (!digits) return "Telefon raqamini kiriting: +998 dan keyin 9 ta raqam.";
  if (digits.length < 9) {
    return `Raqam to'liq emas: +998 dan keyin 9 ta raqam bo'lishi kerak (hozir ${digits.length} ta). Masalan: +998 (90) 123-45-67.`;
  }
  if (!PHONE_CODES.has(digits.slice(0, 2))) {
    return `«${digits.slice(0, 2)}» — O'zbekiston operator kodi emas. Raqamni +998 (90) 123-45-67 ko'rinishida kiriting.`;
  }
  return "";
}

function fileSize(bytes: number): string {
  return bytes >= MB ? `${(bytes / MB).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/** Faylni qoidalarga tekshiradi (bo'sh satr — fayl yaroqli). */
function fileProblem(file: File): string {
  const extension = file.name.split(".").pop()?.toLowerCase() ?? "";
  const rule = FILE_RULES[extension];
  if (!rule) return `«${file.name}»: faqat JPG, PNG yoki MP4 fayl qabul qilinadi.`;
  if (file.size > rule.maxMb * MB) {
    return `«${file.name}»: ${rule.label} hajmi ${rule.maxMb} MB dan oshmasligi kerak (${fileSize(file.size)}).`;
  }
  return "";
}

let attachmentSeq = 0;
function toAttachment(file: File): Attachment {
  attachmentSeq += 1;
  const kind = file.name.toLowerCase().endsWith(".mp4") ? "video" : "photo";
  return { id: attachmentSeq, file, url: URL.createObjectURL(file), kind };
}

/** «+998 (90) ***-**-67» — tasdiq oynasida raqam to'liq ko'rsatilmaydi. */
function maskedPhone(digits: string): string {
  return `+998 (${digits.slice(0, 2)}) ***-**-${digits.slice(7, 9)}`;
}

/** «6-oktabr, 2026 · 12:03» — qabul qilingan vaqt (server bermasa — hozirgi vaqt). */
function acceptedAt(value: string | undefined, locale: string): string {
  const date = value ? new Date(value) : new Date();
  const time = new Intl.DateTimeFormat(locale, { hour: "2-digit", minute: "2-digit", hour12: false }).format(date);
  return `${formatDate(date, locale)} · ${time}`;
}

function placeText(place: AppealPlace | undefined): string {
  if (!place) return "";
  return [place.region, place.district].filter(Boolean).join(", ");
}

/** Backend xatolaridan maydon kesimidagi matnlarni ajratadi. */
function serverFieldErrors(payload: unknown): Errors {
  const details = (payload as { error?: { details?: Record<string, unknown> } } | null)?.error
    ?.details;
  if (!details || typeof details !== "object") return {};
  const pick = (key: string) => {
    const value = details[key];
    return Array.isArray(value) ? String(value[0]) : typeof value === "string" ? value : "";
  };
  const errors: Errors = {};
  if (pick("appeal_type")) errors.type = pick("appeal_type");
  if (pick("phone")) errors.phone = pick("phone");
  if (pick("lat") || pick("lon")) errors.location = pick("lat") || pick("lon");
  if (pick("description")) errors.comment = pick("description");
  if (pick("files")) errors.files = pick("files");
  return errors;
}

export function AppealForm({
  home,
  onTrack,
}: {
  home: PublicHome | null;
  /** Yuborilgan murojaat ID si bilan kuzatish sahifasini ochish. */
  onTrack: (appealId: string) => void;
}) {
  const { lang, locale } = useI18n();
  const types = useAppealTypes();
  const map = usePublicMap();
  const captchaBox = useRef<HTMLDivElement>(null);
  const captcha = useCaptcha(captchaBox, home?.captcha, lang);

  const [type, setType] = useState("");
  const [phone, setPhone] = useState("");
  const [point, setPoint] = useState<{ lat: number; lon: number } | null>(null);
  const [pointSource, setPointSource] = useState<"map" | "gps">("map");
  const [accuracy, setAccuracy] = useState<number | null>(null);
  const [place, setPlace] = useState<PlaceState>({ phase: "idle" });
  const [focus, setFocus] = useState<MapFocus | null>(null);
  const [address, setAddress] = useState("");
  const [comment, setComment] = useState("");
  const [files, setFiles] = useState<Attachment[]>([]);
  const [preview, setPreview] = useState<Attachment | null>(null);
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState<Errors>({});
  const [formError, setFormError] = useState("");
  const [canRetry, setCanRetry] = useState(false);
  const [locating, setLocating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [upload, setUpload] = useState<{ phase: UploadPhase; ratio: number }>({ phase: "idle", ratio: 0 });
  const [result, setResult] = useState<{ created: AppealCreated; phone: string; type: string } | null>(null);
  const [copied, setCopied] = useState(false);
  /* Ikki marta tez bosilganda holat hali yangilanmagan bo'ladi — ref darhol to'sadi. */
  const sending = useRef(false);
  const replaceInput = useRef<HTMLInputElement>(null);
  const replaceTarget = useRef<number | null>(null);
  const filesRef = useRef(files);
  filesRef.current = files;

  const basemaps = useMemo(() => {
    const list = map.data?.basemaps?.length ? map.data.basemaps : [publicBasemapFallback];
    return list.map((row) => ({
      code: row.code,
      name: row.name,
      url_template: row.url_template,
      attribution: row.attribution,
    }));
  }, [map.data]);
  const initial = map.data?.initial_view ?? { lat: 41.31, lon: 64.5, zoom: 6 };
  const pin = useMemo(() => (point ? { ...point, label: "Hodisa joyi", tone: "danger" as const } : null), [point]);

  /* Xaritadagi «Shu joy haqida murojaat yuborish» — tanlangan nuqta oldindan belgilanadi
     (foydalanuvchi yuborishdan oldin tekshiradi va o'zgartira oladi). */
  const [params] = useSearchParams();
  useEffect(() => {
    const lat = Number(params.get("lat"));
    const lon = Number(params.get("lon"));
    if (!params.get("lat") || !Number.isFinite(lat) || !Number.isFinite(lon)) return;
    if (Math.abs(lat) > 90 || Math.abs(lon) > 180) return;
    const next = { lat, lon };
    setPoint(next);
    setPointSource("map");
    setFocus({ ...next, zoom: 14, key: `from-map-${lat}-${lon}` });
    if (params.get("joy")) setAddress((current) => current || (params.get("joy") ?? "").slice(0, 500));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Sahifadan chiqilganda ko'rish havolalari bo'shatiladi. */
  useEffect(() => () => filesRef.current.forEach((item) => URL.revokeObjectURL(item.url)), []);

  /* Nuqta o'zgarganda — u tushgan hudud (so'nggi so'rov natijasi qoladi). */
  useEffect(() => {
    if (!point) {
      setPlace({ phase: "idle" });
      return;
    }
    let cancelled = false;
    setPlace({ phase: "loading" });
    const timer = window.setTimeout(() => {
      appealsApi
        .locate(point.lat, point.lon)
        .then((data) => {
          if (!cancelled) setPlace({ phase: "done", data });
        })
        .catch(() => {
          if (!cancelled) setPlace({ phase: "error" });
        });
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [point]);

  function choosePoint(next: { lat: number; lon: number }, source: "map" | "gps", gpsAccuracy: number | null = null) {
    setPoint(next);
    setPointSource(source);
    setAccuracy(gpsAccuracy);
    setErrors((previous) => ({ ...previous, location: undefined }));
  }

  function onPhone(event: ChangeEvent<HTMLInputElement>) {
    const value = event.target.value;
    let digits = value.replace(/\D/g, "");
    if (digits.startsWith("998") && digits.length > 9) digits = digits.slice(3);
    /* Formatlash belgisi («)» yoki «-») o'chirilganda — oxirgi raqam o'chiriladi. */
    if (digits === phone && value.length < formatPhone(phone).length) digits = phone.slice(0, -1);
    digits = digits.slice(0, 9);
    setPhone(digits);
    /* Xato ko'rsatilgan bo'lsa — tuzatilishi bilan darhol yangilanadi. */
    if (errors.phone) setErrors((previous) => ({ ...previous, phone: phoneProblem(digits) || undefined }));
  }

  function onPhoneBlur() {
    if (phone) setErrors((previous) => ({ ...previous, phone: phoneProblem(phone) || undefined }));
  }

  function addFiles(picked: File[]) {
    const next = [...files];
    let problem = "";
    for (const file of picked) {
      const reason = fileProblem(file);
      if (reason) {
        problem = reason;
        continue;
      }
      if (next.length >= MAX_FILES) {
        problem = `Ko'pi bilan ${MAX_FILES} ta fayl biriktirish mumkin.`;
        break;
      }
      next.push(toAttachment(file));
    }
    setFiles(next);
    setUpload({ phase: "idle", ratio: 0 });
    setErrors((previous) => ({ ...previous, files: problem || undefined }));
  }

  function onFiles(event: ChangeEvent<HTMLInputElement>) {
    const picked = Array.from(event.target.files ?? []);
    event.target.value = "";
    addFiles(picked);
  }

  function removeFile(id: number) {
    const target = files.find((item) => item.id === id);
    if (target) URL.revokeObjectURL(target.url);
    setFiles((previous) => previous.filter((item) => item.id !== id));
    setUpload({ phase: "idle", ratio: 0 });
    setErrors((previous) => ({ ...previous, files: undefined }));
  }

  function askReplace(id: number) {
    replaceTarget.current = id;
    replaceInput.current?.click();
  }

  function onReplace(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    const id = replaceTarget.current;
    replaceTarget.current = null;
    if (!file || id === null) return;
    const reason = fileProblem(file);
    if (reason) {
      setErrors((previous) => ({ ...previous, files: reason }));
      return;
    }
    const fresh = toAttachment(file);
    setFiles((previous) =>
      previous.map((item) => {
        if (item.id !== id) return item;
        URL.revokeObjectURL(item.url);
        return fresh;
      }),
    );
    setUpload({ phase: "idle", ratio: 0 });
    setErrors((previous) => ({ ...previous, files: undefined }));
  }

  async function locate() {
    if (!("geolocation" in navigator)) {
      setErrors((previous) => ({
        ...previous,
        location: "Brauzeringiz joylashuvni aniqlashni qo'llab-quvvatlamaydi — nuqtani xaritada bosib belgilang.",
      }));
      return;
    }
    /* Ruxsat oldin rad etilgan bo'lsa, brauzer qayta so'ramaydi — qanday yoqishni aytamiz. */
    try {
      const permission = await navigator.permissions?.query({ name: "geolocation" });
      if (permission?.state === "denied") {
        setErrors((previous) => ({
          ...previous,
          location:
            "Joylashuvni aniqlash brauzerda taqiqlangan. Manzil satridagi qulf belgisi orqali ruxsat bering yoki nuqtani xaritada bosib belgilang.",
        }));
        return;
      }
    } catch {
      /* Permissions API yo'q — to'g'ridan-to'g'ri so'raladi. */
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const next = { lat: position.coords.latitude, lon: position.coords.longitude };
        choosePoint(next, "gps", Math.round(position.coords.accuracy));
        setFocus({ ...next, zoom: 15, key: Date.now() });
        setLocating(false);
      },
      (failure) => {
        const message =
          failure.code === failure.PERMISSION_DENIED
            ? "Joylashuvni aniqlashga ruxsat berilmadi — nuqtani xaritada bosib belgilang."
            : failure.code === failure.TIMEOUT
              ? "Joylashuv belgilangan vaqtda aniqlanmadi. Qayta urinib ko'ring yoki xaritada belgilang."
              : "Joylashuvni aniqlab bo'lmadi — nuqtani xaritada bosib belgilang.";
        setErrors((previous) => ({ ...previous, location: message }));
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 },
    );
  }

  function validate(): Errors {
    const next: Errors = {};
    if (!type) next.type = "Murojaat turini tanlang.";
    const phoneError = phoneProblem(phone);
    if (phoneError) next.phone = phoneError;
    if (!point) {
      next.location =
        "Hodisa joyi belgilanmagan: xaritani bosing yoki «Mening joylashuvim» tugmasidan foydalaning. Nuqtasiz murojaat yuborilmaydi.";
    }
    if (comment.length > MAX_COMMENT) next.comment = `Izoh ${MAX_COMMENT} belgidan oshmasligi kerak.`;
    if (!consent) next.consent = "Shaxsiy ma'lumotlarni qayta ishlashga rozilik bildiring.";
    if (captcha.enabled && !captcha.token) next.captcha = "«Men robot emasman» tekshiruvidan o'ting.";
    return next;
  }

  async function submit(event?: FormEvent) {
    event?.preventDefault();
    if (sending.current) return;
    setFormError("");
    setCanRetry(false);
    const found = validate();
    setErrors(found);
    if (Object.keys(found).length) {
      const first = Object.keys(found)[0];
      document.getElementById(`appeal-${first}`)?.focus();
      return;
    }

    const form = new FormData();
    form.append("appeal_type", type);
    form.append("phone", `+998${phone}`);
    form.append("lat", point!.lat.toFixed(6));
    form.append("lon", point!.lon.toFixed(6));
    if (address.trim()) form.append("address_text", address.trim());
    if (comment.trim()) form.append("description", comment.trim());
    if (captcha.token) form.append("recaptcha_token", captcha.token);
    files.forEach((item) => form.append("files", item.file, item.file.name));

    sending.current = true;
    setBusy(true);
    setUpload({ phase: "uploading", ratio: 0 });
    try {
      const created = await appealsApi.create(form, (ratio) =>
        setUpload({ phase: "uploading", ratio }),
      );
      setUpload({ phase: "done", ratio: 1 });
      setResult({ created, phone, type });
      window.requestAnimationFrame(() => document.getElementById("appeal-result")?.focus());
    } catch (caught) {
      setUpload({ phase: "failed", ratio: 0 });
      if (caught instanceof ApiError) {
        if (caught.isOffline) {
          setFormError("Server bilan aloqa uzildi — murojaat yuborilmadi. Internetni tekshirib, qayta urinib ko'ring yoki 1050 ga qo'ng'iroq qiling.");
          setCanRetry(true);
        } else if (caught.status === 429) {
          setFormError("Yuborish chegarasiga yetildi (bir IP manzildan soatiga 5 ta, bir telefondan sutkasiga 3 ta). Keyinroq urinib ko'ring; shoshilinch holatda 1050 ga qo'ng'iroq qiling.");
        } else if (caught.status >= 500) {
          setFormError("Serverda vaqtinchalik xatolik — murojaat yuborilmadi. Ma'lumotlaringiz saqlangan, qayta urinib ko'ring.");
          setCanRetry(true);
        } else {
          setErrors((previous) => ({ ...previous, ...serverFieldErrors(caught.payload) }));
          setFormError(caught.message);
        }
      } else {
        setFormError("Kutilmagan xatolik yuz berdi. Qayta urinib ko'ring.");
        setCanRetry(true);
      }
      captcha.reset();
    } finally {
      sending.current = false;
      setBusy(false);
    }
  }

  function restart() {
    files.forEach((item) => URL.revokeObjectURL(item.url));
    setResult(null);
    setType("");
    setPhone("");
    setPoint(null);
    setAccuracy(null);
    setAddress("");
    setComment("");
    setFiles([]);
    setUpload({ phase: "idle", ratio: 0 });
    setConsent(false);
    setErrors({});
    setFormError("");
    setCanRetry(false);
    captcha.reset();
  }

  async function copyId() {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result.created.appeal_id);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }

  /* Umumiy yuklanish foizidan har bir faylning ulushi (fayllar ketma-ket yuboriladi). */
  const totalBytes = files.reduce((sum, item) => sum + item.file.size, 0);
  function fileState(index: number): { label: string; tone: string; ratio: number | null } {
    if (upload.phase === "idle") return { label: "Yuborishga tayyor", tone: "", ratio: null };
    if (upload.phase === "failed") return { label: "Yuklanmadi", tone: "is-failed", ratio: null };
    if (upload.phase === "done") return { label: "Yuklandi", tone: "is-done", ratio: 1 };
    const before = files.slice(0, index).reduce((sum, item) => sum + item.file.size, 0);
    const size = files[index].file.size;
    const sent = upload.ratio * totalBytes - before;
    const ratio = Math.max(0, Math.min(1, size ? sent / size : 1));
    if (ratio >= 1) return { label: "Yuklandi", tone: "is-done", ratio: 1 };
    return { label: `Yuklanmoqda… ${Math.round(ratio * 100)}%`, tone: "is-busy", ratio };
  }

  const errorId = (field: Field) => (errors[field] ? `appeal-${field}-error` : undefined);
  const renderError = (field: Field) =>
    errors[field] ? (
      <p className="field-error" id={`appeal-${field}-error`} role="alert">
        <Icon name="alert" size={14} />
        {errors[field]}
      </p>
    ) : null;

  const typeTitle = (code: string) => types.find((item) => item.code === code)?.title ?? code;
  const trackHref = SECTION_PATHS.tracking;

  return (
    <section className="section" id="appeal" aria-labelledby="appeal-title">
      <div className="container">
        <SplitHead
          scene="appeal"
          eyebrow="Murojaat yuborish"
          title="Yong'in yoki qoidabuzarlik haqida xabar bering"
          titleId="appeal-title"
        />

        <div className="appeal-notice" data-reveal>
          <span className="appeal-noauth">
            <Icon name="shield" size={18} />
            <b>OneID orqali kirish talab qilinmaydi</b>
          </span>
          <Link className="appeal-track-link" to={trackHref}>
            <Icon name="target" size={16} />
            Murojaatingiz holatini kuzatish
            <Icon name="arrowRight" size={14} />
          </Link>
        </div>

        <div data-reveal>
        {result ? (
          <div className="appeal-result" id="appeal-result" tabIndex={-1} role="status">
            <span className="appeal-result-icon" aria-hidden="true">
              <Icon name="checkCircle" size={28} />
            </span>
            <div>
              <h3>Murojaatingiz qabul qilindi</h3>
              <p>
                Murojaat ID raqami {maskedPhone(result.phone)} raqamiga SMS orqali yuboriladi. Ushbu
                raqam bilan murojaat holatini kuzatishingiz mumkin.
              </p>
              <div className="appeal-id">
                <span>Murojaat ID:</span>
                <b className="num">{result.created.appeal_id}</b>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => void copyId()}>
                  <Icon name={copied ? "check" : "file"} size={14} />
                  {copied ? "Nusxalandi" : "Nusxa olish"}
                </button>
              </div>
              <dl className="appeal-receipt">
                <div>
                  <dt>Qabul qilingan sana</dt>
                  <dd className="num">{acceptedAt(result.created.created_at, locale)}</dd>
                </div>
                <div>
                  <dt>Holati</dt>
                  <dd>
                    <span className="badge badge-info">
                      {result.created.status === "new" || !result.created.status_label
                        ? "Yangi"
                        : result.created.status_label}
                    </span>
                  </dd>
                </div>
                <div>
                  <dt>Murojaat turi</dt>
                  <dd>{typeTitle(result.type)}</dd>
                </div>
                {placeText(result.created.place) ? (
                  <div>
                    <dt>Hudud</dt>
                    <dd>{placeText(result.created.place)}</dd>
                  </div>
                ) : null}
              </dl>
              {result.created.attachments ? (
                <p className="hint">
                  {result.created.attachments} ta fayl antivirus tekshiruvidan o'tib biriktirildi.
                </p>
              ) : null}
              <div className="hero-actions">
                <button type="button" className="btn btn-primary" onClick={() => onTrack(result.created.appeal_id)}>
                  <Icon name="target" size={16} />
                  Holatni kuzatish
                </button>
                <button type="button" className="btn btn-outline" onClick={restart}>
                  Yangi murojaat
                </button>
              </div>
            </div>
          </div>
        ) : (
          <form className="appeal-form" onSubmit={(event) => void submit(event)} noValidate aria-busy={busy}>
            <fieldset className="appeal-step" disabled={busy}>
              <legend>
                <span className="step-num" aria-hidden="true">1</span>
                Murojaat turi <span className="admin-required" aria-hidden="true">*</span>
                <span className="visually-hidden">(majburiy)</span>
              </legend>
              <div
                className="appeal-types-grid"
                role="radiogroup"
                aria-describedby={errorId("type")}
                aria-invalid={Boolean(errors.type)}
              >
                {types.map((item, index) => (
                  <label
                    key={item.code}
                    className={`appeal-type${type === item.code ? " is-selected" : ""}`}
                  >
                    <input
                      type="radio"
                      name="appeal_type"
                      id={index === 0 ? "appeal-type" : undefined}
                      value={item.code}
                      checked={type === item.code}
                      onChange={() => {
                        setType(item.code);
                        setErrors((previous) => ({ ...previous, type: undefined }));
                      }}
                    />
                    <span className="dot" style={{ background: item.color }} aria-hidden="true" />
                    <span className="appeal-type-text">
                      <b>{item.title}</b>
                      {item.sla ? <small>Javob: {item.sla}</small> : null}
                    </span>
                  </label>
                ))}
              </div>
              {renderError("type")}
            </fieldset>

            <fieldset className="appeal-step" disabled={busy}>
              <legend>
                <span className="step-num" aria-hidden="true">2</span>
                Hodisa joyi <span className="admin-required" aria-hidden="true">*</span>
                <span className="visually-hidden">(majburiy)</span>
              </legend>
              <p className="hint">
                Xaritani bosib nuqtani belgilang yoki «Mening joylashuvim» tugmasini bosing. Nuqtani
                sudrab yoki boshqa joyni bosib o'zgartirish mumkin.
              </p>
              <div className={`appeal-map${errors.location ? " is-invalid" : ""}`}>
                <MapView
                  basemaps={basemaps}
                  center={{ lat: initial.lat, lon: initial.lon }}
                  zoom={initial.zoom}
                  focus={focus}
                  pin={pin}
                  onPinDrag={(next) => choosePoint(next, "map")}
                  onMapClick={(next) => choosePoint(next, "map")}
                  popup={false}
                />
              </div>
              <div className="appeal-location-row">
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  id="appeal-location"
                  onClick={() => void locate()}
                  disabled={locating}
                  aria-describedby={["appeal-location-hint", errorId("location")].filter(Boolean).join(" ")}
                >
                  <Icon name="target" size={16} />
                  {locating ? "Aniqlanmoqda…" : "Mening joylashuvim"}
                </button>
                {point ? (
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => {
                      setPoint(null);
                      setAccuracy(null);
                    }}
                  >
                    <Icon name="close" size={14} />
                    Nuqtani olib tashlash
                  </button>
                ) : null}
              </div>
              <span className="hint" id="appeal-location-hint">
                Joylashuv faqat sizning ruxsatingiz bilan aniqlanadi (brauzer so'raydi).
              </span>

              <div className={`appeal-place${point ? " is-set" : ""}`} aria-live="polite">
                {point ? (
                  <>
                    <div className="appeal-place-row">
                      <Icon name="pin" size={16} />
                      <span>
                        <small>Koordinatalar</small>
                        <b className="num">
                          {point.lat.toFixed(5)}, {point.lon.toFixed(5)}
                        </b>
                      </span>
                      <span className="appeal-place-source">
                        {pointSource === "gps"
                          ? `Joylashuvingiz bo'yicha${accuracy ? ` · ±${accuracy} m` : ""}`
                          : "Xaritada belgilandi"}
                      </span>
                    </div>
                    <div className="appeal-place-row">
                      <Icon name="map" size={16} />
                      <span>
                        <small>Aniqlangan hudud</small>
                        {place.phase === "loading" || place.phase === "idle" ? (
                          <b className="muted">Aniqlanmoqda…</b>
                        ) : place.phase === "error" ? (
                          <b className="muted">Hududni aniqlab bo'lmadi — murojaat baribir yuboriladi.</b>
                        ) : place.data?.found ? (
                          <b>
                            {placeText(place.data)}
                            {place.data.forestry ? (
                              <em className="appeal-place-forestry">{place.data.forestry}</em>
                            ) : null}
                          </b>
                        ) : (
                          <b className="appeal-place-warn">
                            Nuqta O'zbekiston ma'muriy hududlaridan tashqarida — joyni tekshiring.
                          </b>
                        )}
                      </span>
                    </div>
                    {pointSource === "gps" ? (
                      <p className="hint">
                        Aniqlangan nuqta hodisa joyiga mos kelmasa, belgini sudrab yoki xaritani bosib
                        to'g'rilang.
                      </p>
                    ) : null}
                  </>
                ) : (
                  <p className="appeal-place-empty">
                    <Icon name="info" size={16} />
                    Nuqta belgilanmagan — nuqtasiz murojaat yuborilmaydi.
                  </p>
                )}
              </div>
              {renderError("location")}
              <span className="field">
                <label className="label" htmlFor="appeal-address">
                  Manzil yoki mo'ljal (ixtiyoriy)
                </label>
                <input
                  id="appeal-address"
                  className="input"
                  maxLength={500}
                  value={address}
                  onChange={(event) => setAddress(event.target.value)}
                  placeholder="Masalan: Bo'stonliq tumani, Chorvoq yo'li, 12-km"
                />
              </span>
            </fieldset>

            <fieldset className="appeal-step" disabled={busy}>
              <legend>
                <span className="step-num" aria-hidden="true">3</span>
                Aloqa va tafsilotlar
              </legend>
              <span className="field">
                <label className="label" htmlFor="appeal-phone">
                  Telefon raqami <span className="admin-required" aria-hidden="true">*</span>
                </label>
                <span className={`input-group phone-group${errors.phone ? " is-invalid" : ""}`}>
                  <span className="phone-prefix" aria-hidden="true">+998</span>
                  <input
                    id="appeal-phone"
                    className="input num"
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel-national"
                    placeholder="(90) 123-45-67"
                    value={formatPhone(phone)}
                    onChange={onPhone}
                    onBlur={onPhoneBlur}
                    aria-required="true"
                    aria-invalid={Boolean(errors.phone)}
                    aria-describedby={[errorId("phone"), "appeal-phone-hint"].filter(Boolean).join(" ")}
                  />
                </span>
                {renderError("phone")}
                <span className="hint" id="appeal-phone-hint">
                  Format: +998 (XX) XXX-XX-XX. Murojaat ID va holati haqidagi xabarlar shu raqamga
                  SMS orqali keladi. Raqam xeshlangan holda saqlanadi.
                </span>
              </span>

              <span className="field">
                <label className="label" htmlFor="appeal-comment">
                  Izoh (ixtiyoriy)
                </label>
                <textarea
                  id="appeal-comment"
                  className="input"
                  rows={4}
                  maxLength={MAX_COMMENT}
                  value={comment}
                  onChange={(event) => setComment(event.target.value)}
                  aria-describedby={["appeal-comment-count", errorId("comment")].filter(Boolean).join(" ")}
                  placeholder="Nima bo'layotganini qisqacha yozing: tutun, kesilgan daraxtlar soni, transport raqami…"
                />
                <span className="hint num" id="appeal-comment-count">
                  {comment.length} / {MAX_COMMENT}
                </span>
                {renderError("comment")}
              </span>

              <span className="field">
                <span className="label" id="appeal-files-label">
                  Foto yoki video (ixtiyoriy)
                </span>
                {files.length < MAX_FILES ? (
                  <label className="file-drop" htmlFor="appeal-files">
                    <Icon name="camera" size={20} />
                    <span>
                      <b>{files.length ? "Yana fayl qo'shish" : "Fayl tanlash"}</b>
                      <small>
                        JPG, PNG — 20 MB gacha; MP4 — 50 MB gacha; ko'pi bilan {MAX_FILES} ta
                        {files.length ? ` (tanlangan: ${files.length})` : ""}
                      </small>
                    </span>
                  </label>
                ) : (
                  <p className="hint">Fayllar soni chegarasiga yetildi ({MAX_FILES} ta).</p>
                )}
                <input
                  id="appeal-files"
                  className="visually-hidden"
                  type="file"
                  multiple
                  accept={FILE_ACCEPT}
                  onChange={onFiles}
                  aria-labelledby="appeal-files-label"
                  aria-describedby={errorId("files")}
                />
                <input
                  ref={replaceInput}
                  className="visually-hidden"
                  type="file"
                  accept={FILE_ACCEPT}
                  onChange={onReplace}
                  tabIndex={-1}
                  aria-hidden="true"
                />
                {files.length ? (
                  <ul className="file-list" aria-label="Biriktirilgan fayllar">
                    {files.map((item, index) => {
                      const state = fileState(index);
                      return (
                        <li key={item.id} className={state.tone}>
                          <button
                            type="button"
                            className="file-thumb"
                            onClick={() => setPreview(item)}
                            aria-label={`${item.file.name} — ko'rish`}
                          >
                            {item.kind === "photo" ? (
                              <img src={item.url} alt="" />
                            ) : (
                              <Icon name="play" size={18} />
                            )}
                          </button>
                          <span className="file-meta">
                            <span className="file-name" title={item.file.name}>{item.file.name}</span>
                            <span className="file-sub">
                              <span className="num">{fileSize(item.file.size)}</span>
                              <span aria-hidden="true">·</span>
                              <span>{item.kind === "photo" ? "Foto" : "Video"}</span>
                            </span>
                            <span className="file-status">{state.label}</span>
                            {state.ratio !== null && state.tone === "is-busy" ? (
                              <span
                                className="file-progress"
                                role="progressbar"
                                aria-label={`${item.file.name} yuklanmoqda`}
                                aria-valuemin={0}
                                aria-valuemax={100}
                                aria-valuenow={Math.round(state.ratio * 100)}
                              >
                                <span style={{ width: `${Math.round(state.ratio * 100)}%` }} />
                              </span>
                            ) : null}
                          </span>
                          <span className="file-actions">
                            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setPreview(item)}>
                              <Icon name="eye" size={14} />
                              <span className="file-action-text">Ko'rish</span>
                            </button>
                            <button
                              type="button"
                              className="btn btn-ghost btn-sm"
                              onClick={() => askReplace(item.id)}
                              aria-label={`${item.file.name} faylini almashtirish`}
                            >
                              <Icon name="refresh" size={14} />
                              <span className="file-action-text">Almashtirish</span>
                            </button>
                            <button
                              type="button"
                              className="btn btn-ghost btn-sm file-remove"
                              onClick={() => removeFile(item.id)}
                              aria-label={`${item.file.name} faylini o'chirish`}
                            >
                              <Icon name="close" size={14} />
                              <span className="file-action-text">O'chirish</span>
                            </button>
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                ) : null}
                {renderError("files")}
              </span>
            </fieldset>

            <div className="appeal-submit">
              {captcha.enabled ? (
                <div className="captcha-box">
                  <div ref={captchaBox} id="appeal-captcha" tabIndex={-1} />
                  {captcha.error ? <p className="field-error">{captcha.error}</p> : null}
                  {renderError("captcha")}
                </div>
              ) : null}

              <label className="checkbox">
                <input
                  id="appeal-consent"
                  type="checkbox"
                  checked={consent}
                  disabled={busy}
                  onChange={(event) => {
                    setConsent(event.target.checked);
                    setErrors((previous) => ({ ...previous, consent: undefined }));
                  }}
                  aria-describedby={errorId("consent")}
                />
                <span>
                  Telefon raqamim murojaatni ko'rib chiqish va SMS xabarnoma yuborish uchun
                  qayta ishlanishiga roziman.
                </span>
              </label>
              {renderError("consent")}

              {formError ? (
                <div className="alert alert-error appeal-error" role="alert">
                  <Icon name="alert" size={18} />
                  <span>{formError}</span>
                  {canRetry ? (
                    <button type="button" className="btn btn-outline btn-sm" onClick={() => void submit()}>
                      <Icon name="refresh" size={14} />
                      Qayta urinish
                    </button>
                  ) : null}
                </div>
              ) : null}

              <button
                className="btn btn-primary btn-lg"
                type="submit"
                disabled={busy}
              >
                {busy ? <span className="btn-spinner" aria-hidden="true" /> : <Icon name="send" size={18} />}
                {busy
                  ? files.length && upload.ratio < 1
                    ? `Yuborilmoqda… ${Math.round(upload.ratio * 100)}%`
                    : "Yuborilmoqda…"
                  : "Murojaatni yuborish"}
              </button>

              <p className="appeal-footnote">
                <Icon name="info" size={14} />
                <span>
                  Murojaat qabul qilingach, ID raqami telefoningizga SMS orqali yuboriladi. Ushbu
                  raqam bilan murojaat holatini <Link to={trackHref}>kuzatishingiz</Link> mumkin.
                </span>
              </p>
              <p className="hint appeal-limits">
                <Icon name="shield" size={14} /> Himoya: bir IP manzildan soatiga 5 ta, bir telefondan
                sutkasiga 3 ta murojaat; fayllar antivirus tekshiruvidan o'tadi.
              </p>
            </div>
          </form>
        )}
        </div>
      </div>

      <Modal
        open={Boolean(preview)}
        title={preview?.file.name ?? ""}
        onClose={() => setPreview(null)}
        size="lg"
      >
        {preview ? (
          <div className="file-preview">
            {preview.kind === "photo" ? (
              <img src={preview.url} alt={`Biriktirilgan foto: ${preview.file.name}`} />
            ) : (
              <video src={preview.url} controls preload="metadata" />
            )}
            <p className="hint num">
              {fileSize(preview.file.size)} · {preview.kind === "photo" ? "Foto" : "Video"}
            </p>
          </div>
        ) : null}
      </Modal>
    </section>
  );
}
